/**
 * The three ways this set draws a 1px rule, and why none of them draws it with
 * a hard stop.
 *
 * ## The moire
 *
 * `${INK} 0 1px, transparent 1px 40px` is the obvious way to write a hairline
 * and it is wrong on a large share of real screens. A CSS pixel is normalised,
 * so a 1px rule is one device pixel at DPR 1 and two at DPR 2 and looks
 * identical at both. The trouble is the fractional ratios in between — 1.25
 * and 1.5 are what Windows laptops and most Android phones report, and browser
 * zoom lands on them too. There a 1px hard-edged rule is 1.25 device pixels,
 * which cannot be drawn, so the renderer rounds it: some rules in the tile land
 * on 1 device pixel and some on 2, in a pattern that beats against the tile
 * period. The band gets visible bands of heavier and lighter ruling that move
 * when the page is zoomed.
 *
 * Feathering the stops is the fix. With a 0.6px ramp either side of the core
 * the renderer has a gradient to sample instead of a binary edge, so the
 * rounding shows up as a sub-pixel difference in weight rather than as a rule
 * that is twice as thick as its neighbour. `dots` worked this out already and
 * says so in its own note; this is that reasoning applied to the seven motifs
 * built from lines, which never got it.
 *
 * ## Centred on the boundary
 *
 * All three helpers put the rule's core astride the tile boundary — half of it
 * at the end of one tile and half at the start of the next — rather than at the
 * tile's leading edge. That is what lets both sides of the rule be feathered:
 * a rule sitting at `0 1px` has the tile wrap hard against its left edge, and
 * no stop can soften a seam that a repeat is going to cut anyway. It moves the
 * whole motif by half a pixel, which is not a design decision; the phase of a
 * repeating texture is not visible.
 */

/** Half the 1px core, measured out from the tile boundary in both directions. */
const CORE = '0.5px';

/** Where the feather has faded to nothing: a 0.6px ramp past the core. */
const EDGE = '1.1px';

/**
 * A feathered rule crossing the gradient line every `period`.
 *
 * For the motifs whose spacing is the gradient's own repeat — the isometric
 * lattice, the diagonals, the crosshatch. `repeating-linear-gradient` measures
 * its period perpendicular to the gradient line, so an angled family needs no
 * tile-size trigonometry to stay seamless.
 */
export const rule = (angle: string, period: string, ink: string) =>
  `repeating-linear-gradient(${angle}, ${ink} 0 ${CORE}, transparent ${EDGE} calc(${period} - ${EDGE}), ${ink} calc(${period} - ${CORE}) ${period})`;

/**
 * A feathered rule at the edge of each background tile, for the motifs spaced
 * by `backgroundSize` instead — the grid and the uprights.
 *
 * The plateau is stated in `%` and the core in px on purpose: that is what
 * lets one gradient string be reused at two tile sizes, so the grid's major
 * and minor rules are the same declaration painted at 128px and at 32px and
 * cannot drift apart in weight.
 */
export const tiledRule = (direction: string, ink: string) =>
  `linear-gradient(${direction}, ${ink} 0 ${CORE}, transparent ${EDGE} calc(100% - ${EDGE}), ${ink} calc(100% - ${CORE}) 100%)`;

/**
 * A feathered arc every `period` out from `origin`.
 *
 * The curved member of the family, and the one that needed this most: a hard
 * stop on a radial gradient aliases along the whole length of the arc rather
 * than at one edge, so the rings stepped visibly even at integer DPR.
 */
export const arc = (origin: string, period: string, ink: string) =>
  `repeating-radial-gradient(circle at ${origin}, ${ink} 0 ${CORE}, transparent ${EDGE} calc(${period} - ${EDGE}), ${ink} calc(${period} - ${CORE}) ${period})`;
